/**
 * Portal SPA types.
 *
 * Two layers live here:
 *  1. WIRE types — the exact JSON shapes from docs/API_CONTRACT.md. These match
 *     the Worker responses byte-for-byte; do not diverge without updating the
 *     contract.
 *  2. UI types — the client-side working models the store and screens use
 *     (LocalPhoto / UiPrint). These carry extra local state (File handles,
 *     object-URL previews, decode/upload status) that never goes over the wire.
 *
 * Pricing types (PaperId, Totals, CartGroup, PricingData) come from @rpl/pricing.
 */

import type { BorderMode, PaperId, Totals } from '@rpl/pricing';
import type { Rotation } from './rotate';

export type { BorderMode };

export type BwStyle = 'v1' | 'v2' | 'v3';
export type { Rotation };

/* ============================================================
   Shared enums / conventions (API_CONTRACT.md "Shared enums")
   ============================================================ */

export type FitMode = 'crop' | 'fit';
export type PrintCheckStatus = 'ok' | 'autocrop' | 'lowres';

/** Server-side upload lifecycle for a photo row. */
export type WireUploadStatus = 'pending' | 'uploaded' | 'failed';

/** Public order status enum (GET /orders). */
export type OrderStatus =
  | 'received'
  | 'ready'
  | 'shipped'
  | 'collected'
  // internal, still possible to receive:
  | 'cancelled'
  | 'needs_review'
  // richer timeline statuses used by the confirmation timeline design:
  | 'printing'
  | 'quality_check';

/** Normalized crop rectangle, all in [0,1] over the EXIF-corrected image. */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Pre-Add adjustments STAGED from the Customise ✎ dialog (owner 2026-07-27):
 *  Save there must not add to the order — these apply when Add creates the
 *  prints, then clear. `sizeId` is the size the crop was made at (aspect
 *  guard: a crop staged at one shape never applies verbatim to another). */
export interface StagedAdjust {
  sizeId: string;
  fitMode: FitMode;
  cropRect: CropRect | null;
  /** 0|90|180|270 — the crop rect lives in the ROTATED image space. */
  rotation: number;
  bw: boolean;
  bwStyle?: BwStyle | null;
  bwStrength?: number | null;
  /** Border chosen in the staged dialog (owner 2026-07-31); absent = untouched. */
  borderMode?: BorderMode;
  borderMm?: number | null;
}

export type FulfilmentKind = 'pickup_sydney' | 'pickup_wollongong' | 'ship';

/* ============================================================
   WIRE types — request/response bodies
   ============================================================ */

export interface WirePhoto {
  photo_id: string;
  filename: string;
  width: number;
  height: number;
  bytes: number;
  sha256: string;
  upload_status: WireUploadStatus;
  sort_order: number;
}

export interface WirePrint {
  print_id: string;
  photo_id: string;
  size_id: string;
  paper: PaperId;
  qty: number;
  fit_mode: FitMode;
  crop_rect: CropRect | null;
  print_check: PrintCheckStatus;
  override_lowres: boolean;
  effective_dpi: number;
  unit_price: number;
  frame_id?: string;
  /** Border rendered at bake — none (edge, default) | white | handling. */
  border_mode?: BorderMode;
  border_mm?: number | null;
  /** Clockwise rotation applied to the EXIF-corrected image BEFORE crop. */
  rotation?: number;
  /** true = convert to black & white at bake. */
  bw?: boolean;
  /** B&W style (owner 2026-07-31): v1 classic | v2 dramatic | v3 soft. */
  bw_style?: BwStyle | null;
  /** v2/v3 strength 0..100 step 10; null/absent = 50. Never set for v1. */
  bw_strength?: number | null;
}

/** PATCH /projects/:id — one entry in the replace-set prints array. */
export interface PrintUpsert {
  print_id?: string;
  photo_id: string;
  size_id: string;
  paper: PaperId;
  qty: number;
  fit_mode?: FitMode;
  crop_rect?: CropRect | null;
  print_check: PrintCheckStatus;
  override_lowres?: boolean;
  effective_dpi?: number;
  frame_id?: string;
  border_mode?: BorderMode;
  border_mm?: number | null;
  rotation?: Rotation;
  bw?: boolean;
  bw_style?: BwStyle | null;
  bw_strength?: number | null;
}

export interface ProjectState {
  project_id: string;
  status: string;
  email: string | null;
  ui_state: UiStatePersist | Record<string, unknown> | null;
  photos: WirePhoto[];
  prints: WirePrint[];
  created_at: string;
  updated_at: string;
}

export interface CreateProjectResponse {
  project_id: string;
  token: string;
}

export interface CreatePhotoRequest {
  filename: string;
  bytes: number;
  sha256: string;
  width: number;
  height: number;
}

export type OriginalUploadTarget =
  | { mode: 'single'; put_url: string }
  | { mode: 'mpu'; key: string; upload_id: string; part_size: number };

export interface CreatePhotoResponse {
  photo_id: string;
  preview_put_url: string;
  original: OriginalUploadTarget;
}

export interface MpuPartResponse {
  put_url: string;
}

export interface MpuCompleteRequest {
  uploadId: string;
  parts: Array<{ partNumber: number; etag: string }>;
}

export interface PatchProjectRequest {
  prints?: PrintUpsert[];
  ui_state?: UiStatePersist | Record<string, unknown>;
}

export interface PatchProjectResponse {
  prints: WirePrint[];
  totals: Totals;
}

export interface SaveProjectResponse {
  ok: true;
  resume_url: string;
}

export interface CartItem {
  id: number;
  quantity: number;
  properties: Record<string, string>;
}

export interface CheckoutResponse {
  cart: { items: CartItem[] };
  totals: Totals;
}

/** POST /orders/:orderName/share — mint (or reuse) a share link. */
export interface MintShareResponse {
  share_url: string;
  expires_at: string;
}

/** POST /shares/accept — clone the shared photos into a fresh project. */
export interface AcceptShareResponse {
  project_id: string;
  token: string;
  photo_count: number;
}

export interface OrderStatusResponse {
  order_name: string;
  status: OrderStatus;
  timeline: Array<{ status: OrderStatus; at: string }>;
  fulfilment: FulfilmentKind;
  eta: string | null;
  tracking_url: string | null;
  customer_first: string;
  summary: Array<{ label: string; qty: number; detail: string }>;
}

/** Persisted slice of UI state (PATCH ui_state). Opaque to the server. */
export interface UiStatePersist {
  step?: number;
  /** Customer instructions for the lab (cart "Add Instructions" dialog). */
  orderNote?: string;
  /** Urgent-processing upsell toggle (cart urgency dialog) — carried in the checkout body too. */
  urgent?: boolean;
  applyBar?: { sizeId: string | null; paper: PaperId; qty: number };
  sort?: SortMode;
  guideSeen?: boolean;
}

/* ============================================================
   UI types — client working models
   ============================================================ */

/** Decode/upload lifecycle as seen in the grid (superset of the wire status). */
export type PhotoUiStatus =
  | 'queued' // accepted, awaiting decode
  | 'decoding' // reading dimensions + building preview
  | 'hashing' // computing sha256
  | 'uploading' // transferring to R2
  | 'ready' // fully uploaded + confirmed
  | 'error'; // rejected or failed

export interface LocalPhoto {
  /** Stable client id — the React key and the id UiPrint.photoId references. */
  id: string;
  /** Server photo_id, present once POST /photos succeeds. */
  photoId: string | null;
  filename: string;
  /** Original File; null when hydrated from the server on resume. */
  file: File | null;
  /** Object URL (local preview) or server preview URL. */
  previewUrl: string | null;
  width: number;
  height: number;
  /** Megapixels (width*height/1e6), not rounded — round at display time. */
  mp: number;
  bytes: number;
  sha256: string | null;
  status: PhotoUiStatus;
  /** Upload progress 0..100. */
  progress: number;
  /** Friendly, user-facing error message when status === 'error'. */
  error?: string;
  sortOrder: number;
}

/**
 * One print in the working order. `photoId` references LocalPhoto.id (the client
 * id). The store maps it to the server photo_id when building PrintUpserts for
 * autosave / checkout. A print may be framed (frameId set) per the FRAMING MODEL.
 */
export interface UiPrint {
  id: string;
  printId: string | null;
  photoId: string;
  sizeId: string;
  paper: PaperId;
  qty: number;
  fitMode: FitMode;
  cropRect: CropRect | null;
  printCheck: PrintCheckStatus;
  overrideLowRes: boolean;
  effectiveDpi: number | null;
  unitPrice: number | null;
  frameId?: string;
  /** Border rendered at bake: none (print-to-edge, default) | white (margin within print) | handling (fine-art extra paper). Framed prints are always 'none'. */
  borderMode?: BorderMode;
  /** Custom white-border width mm (owner 2026-07-31); null/absent = default. */
  borderMm?: number | null;
  /**
   * Clockwise rotation (0|90|180|270) applied to the EXIF-corrected image
   * BEFORE crop — cropRect is defined on the ROTATED image and DPI math uses
   * the rotated dims (w/h swap at 90/270). Default 0.
   */
  rotation?: Rotation;
  /** Convert this print to black & white at bake (preview shows grayscale). */
  bw?: boolean;
  /** B&W style; only meaningful when bw. null/absent = v1 classic. */
  bwStyle?: BwStyle | null;
  /** v2/v3 blend strength 0..100 (step 10, default 50). null on v1/colour. */
  bwStrength?: number | null;
  /**
   * Fine-art border-decision flag (UI-only, not sent on the wire). Fine-art
   * prints start UNDECIDED (false/undefined) and the Continue gate forces a
   * choice; picking any border marks them decided. C-Type/framed prints never
   * gate, so this is irrelevant for them.
   */
  borderDecided?: boolean;
  /**
   * What this print WAS before it was framed (UI-only, not sent on the wire,
   * same as borderDecided). convertToFramed overwrites size, paper, crop,
   * border and the Print Check with the FRAME'S values, so without this
   * snapshot "Remove frame" has nothing to go back to and leaves a loose print
   * at the frame's size — which, for the upsell that triggered this (owner
   * 2026-07-27), is a size the photo could not carry in the first place.
   * Cleared once consumed, and on an ambiguous merge.
   */
  framedFrom?: FramedFrom | null;
}

/** The pre-frame state of a print — see UiPrint.framedFrom. */
export interface FramedFrom {
  sizeId: string;
  paper: PaperId;
  fitMode: FitMode;
  cropRect: CropRect | null;
  printCheck: PrintCheckStatus;
  effectiveDpi: number | null;
  overrideLowRes: boolean;
  borderMode: BorderMode;
  borderDecided: boolean;
}

/** Grid sort. `'todo'` (Step-2 default) floats photos without a print to the top. */
export type SortMode = 'upload' | 'name' | 'mp-desc' | 'todo';

/** Ordering-flow steps (index into the stepper). */
export const STEPS = ['Upload', 'Size & Paper', 'Cart', 'Checkout'] as const;
export type StepIndex = 0 | 1 | 2 | 3;
